import { Button, Descriptions, Empty, Input, Modal, Space, Switch, Table, Tag, notification } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import PageTitle from 'components/page-title';
import { ROLE_USER } from 'constants/common';
import { API } from 'service/api-route';
import { Department, isDepartmentActive } from 'types/department';
import AxiosUtils from 'utils/axios-utils';
import DepartmentFormModal from './department-form-modal';
import { getDepartmentError } from './department-error';
import styles from './styles.module.scss';

function getRoleValues(response: any): string[] {
  const user = response?.data?.user || response?.data || response?.user || response;
  const roles = user?.roles || user?.role;
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string') return roles.split(',').map((role) => role.trim());
  return [];
}

const DepartmentManager = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [canWrite, setCanWrite] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Department | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Department | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const refresh = () => setRefreshKey((key) => key + 1);

  useEffect(() => {
    let cancelled = false;
    const fetchRole = async () => {
      try {
        const response = await AxiosUtils.get(API.getUserInfo);
        if (!cancelled) setCanWrite(getRoleValues(response).includes(ROLE_USER.ADMIN));
      } catch {
        if (!cancelled) setCanWrite(false);
      }
    };
    fetchRole();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchDepartments = async () => {
      setLoading(true);
      setListError(false);
      try {
        const response = await AxiosUtils.get<
          { search?: string; includeInactive: string },
          { data: Department[] }
        >(API.departments, {
          ...(search ? { search } : {}),
          includeInactive: String(includeInactive),
        });
        if (!Array.isArray(response.data)) throw new Error('Invalid departments response');
        if (!cancelled) setDepartments(response.data);
      } catch (error) {
        if (!cancelled) {
          setDepartments([]);
          setListError(true);
          notification.error({ message: getDepartmentError(error) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDepartments();
    return () => {
      cancelled = true;
    };
  }, [search, includeInactive, refreshKey]);

  useEffect(() => {
    if (detailId === null) return;
    let cancelled = false;
    const fetchDetail = async () => {
      setDetail(null);
      setDetailLoading(true);
      try {
        const response = await AxiosUtils.get<undefined, { data: Department }>(
          `${API.departments}/${detailId}`
        );
        if (!cancelled) setDetail(response.data);
      } catch (error) {
        if (!cancelled) {
          notification.error({ message: getDepartmentError(error) });
          setDetailId(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [detailId]);

  const confirmDelete = (department: Department) => {
    Modal.confirm({
      title: `Xoá bộ phận ${department.name}?`,
      content: 'Bộ phận sẽ được xoá mềm và không còn xuất hiện trong danh sách.',
      okText: 'Xoá bộ phận',
      okType: 'danger',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await AxiosUtils._delete(`${API.departments}/${department.id}`, undefined);
          notification.success({ message: 'Đã xoá bộ phận.' });
          refresh();
        } catch (error) {
          notification.error({ message: getDepartmentError(error) });
        }
      },
    });
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await AxiosUtils.post<undefined, { data: { created: string[]; skipped: string[] } }>(
        `${API.departments}/seed`
      );
      notification.success({
        message: `Đã thêm ${response.data.created.length} bộ phận mặc định.`,
        description: `${response.data.skipped.length} mã đã có được bỏ qua.`,
      });
      refresh();
    } catch (error) {
      notification.error({ message: getDepartmentError(error) });
    } finally {
      setSeeding(false);
    }
  };

  const columns: ColumnsType<Department> = [
    { title: 'Thứ tự', dataIndex: 'sortOrder', key: 'sortOrder', width: 85 },
    { title: 'Mã', dataIndex: 'code', key: 'code', width: 160 },
    { title: 'Tên bộ phận', dataIndex: 'name', key: 'name', width: 200 },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Trạng thái',
      key: 'isActive',
      width: 125,
      render: (_: unknown, record: Department) =>
        isDepartmentActive(record) ? <Tag color="green">Đang bật</Tag> : <Tag>Đã tắt</Tag>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: canWrite ? 230 : 100,
      render: (_: unknown, record: Department) => (
        <Space size="small">
          <Button type="link" onClick={() => setDetailId(record.id)}>Chi tiết</Button>
          {canWrite && (
            <>
              <Button type="link" onClick={() => setEditTarget(record)}>Sửa</Button>
              <Button type="link" danger onClick={() => confirmDelete(record)}>Xoá</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="Quản lý bộ phận" />
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Input.Search
            aria-label="Tìm bộ phận theo mã hoặc tên"
            placeholder="Tìm mã hoặc tên bộ phận"
            value={searchInput}
            allowClear
            onChange={(event) => setSearchInput(event.target.value)}
            onSearch={(value) => setSearch(value.trim())}
            enterButton="Tìm"
            className={styles.search}
          />
          <label className={styles.switchLabel}>
            <Switch checked={includeInactive} onChange={setIncludeInactive} />
            Hiện bộ phận đã tắt
          </label>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Tải lại</Button>
          {canWrite && (
            <>
              <Button onClick={seedDefaults} loading={seeding}>Khởi tạo mặc định</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                Thêm bộ phận
              </Button>
            </>
          )}
        </Space>
      </div>
      <div className="custom-table custom-form">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={departments}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 900 }}
          locale={{ emptyText: listError ? 'Không tải được danh sách bộ phận.' : <Empty description="Không có bộ phận phù hợp." /> }}
        />
      </div>
      <DepartmentFormModal
        open={createOpen || editTarget !== null}
        department={editTarget}
        onClose={() => { setCreateOpen(false); setEditTarget(null); }}
        onSuccess={refresh}
      />
      <Modal
        title="Chi tiết bộ phận"
        open={detailId !== null}
        onCancel={() => setDetailId(null)}
        footer={<Button onClick={() => setDetailId(null)}>Đóng</Button>}
        width={600}
      >
        {detailLoading ? 'Đang tải...' : detail && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Mã">{detail.code}</Descriptions.Item>
            <Descriptions.Item label="Tên">{detail.name}</Descriptions.Item>
            <Descriptions.Item label="Mô tả">{detail.description || '—'}</Descriptions.Item>
            <Descriptions.Item label="Thứ tự">{detail.sortOrder}</Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              {isDepartmentActive(detail) ? 'Đang bật' : 'Đã tắt'}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày tạo">
              {dayjs(detail.createdAt).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Cập nhật">
              {dayjs(detail.updatedAt).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default DepartmentManager;
